import * as React from 'react'
import { FunctionComponent, PropsWithChildren, ReactElement, useMemo } from 'react'
import { Form as FormikForm, useFormikContext } from 'formik'
import Loading from '../../components/Loading'
import { NormalizedError } from '../../reducers/errorReducer'
import { FormikConfig } from 'formik/dist/types'
import { DataPromise } from '../../context/AxiosContext'
import {
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarGroup,
    MessageBarTitle,
    shorthands,
    tokens,
} from '@fluentui/react-components'
import { getId } from '@fluentui/react'
import { useTranslation } from 'react-i18next'

// A return URL is required to determine the location if we don't want to return to the same location as our origin.
export type EntityFormPage = { returnUrl?: string; useFileContext?: boolean }
export type EntityFormProps<E, V, S = null> = PropsWithChildren<{
    entity?: DataPromise<E, S>
    initialValues?: Partial<V>
    useFileContext?: boolean
    returnUrl?: string
    onSubmit: FormikConfig<V>['onSubmit']
}>
export type EntityFormWrapperProps<E, V> = EntityFormPage & { form: FunctionComponent<EntityFormProps<E, V>> }

export type EntityParams = {
    id?: string
}

type Props = PropsWithChildren<{
    noLoading?: boolean // If we don't want to show a loading message when persisting the form.
    className?: string
    status?: string
    globalErrors?: NormalizedError[]
}>

const flatMapError = error => {
    if (!error || error instanceof Function) {
        return []
    } else if (Array.isArray(error)) {
        return error.flatMap(flatMapError)
    } else if (typeof error === 'object') {
        return Object.values(error).flatMap(flatMapError)
    } else {
        return [error]
    }
}

const useStyles = makeStyles({
    childrenContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    spacingHorizontal: {
        ...shorthands.margin(tokens.spacingHorizontalM),
    },
})

function Errors<E>(props: { errors?: E[]; message: string; children: (error: E) => ReactElement }): ReactElement {
    const styles = useStyles()

    if (!props.errors?.length) {
        return <></>
    }

    return (
        <MessageBarGroup>
            <MessageBar intent={'error'} className={styles.spacingHorizontal}>
                <MessageBarBody>
                    <MessageBarTitle>{props.message}</MessageBarTitle>
                    <ul style={{ listStylePosition: 'inside' }}>
                        {props.errors?.map((error: E) => <li key={getId('error')}>{props.children(error)}</li>)}
                    </ul>
                </MessageBarBody>
            </MessageBar>
        </MessageBarGroup>
    )
}

const GlobalError = ({ error }: { error: NormalizedError }): ReactElement => (
    <>
        {error.source && `${error.source}: `}
        {error.message}
    </>
)

export default function Form(props: Props): ReactElement {
    const { t: translate } = useTranslation()
    const { errors, isSubmitting } = useFormikContext()
    const styles = useStyles()

    const formErrors = useMemo<string[]>(() => {
        if (Object.keys(errors || {}).length === 0) {
            return []
        }
        return Object.values(errors).flatMap(flatMapError)
    }, [errors])

    if (isSubmitting && props.noLoading === false && !props.globalErrors) {
        return <Loading label={props.status || translate('base.saving')} />
    }

    return (
        <FormikForm noValidate className={props.className}>
            <Errors<NormalizedError>
                errors={props.globalErrors}
                message={translate('base.form.errors.error', { count: props.globalErrors?.length ?? 1 })}
            >
                {error => <GlobalError key={getId('error')} error={error} />}
            </Errors>
            <Errors<string>
                errors={formErrors}
                message={translate('base.form.field.error', { count: formErrors?.length ?? 1 })}
            >
                {error => <>{error}</>}
            </Errors>
            <div className={styles.childrenContainer}>{props.children}</div>
        </FormikForm>
    )
}
